import { useCallback, useEffect, useRef, useState } from 'react';
import BackendService from '../../../services/admin/BackendService';
import { InvestorStatementBatch } from '../../../models/admin/InvestorStatement';

const POLL_INTERVAL_MS = 2000;

const confirmedCount = (items: InvestorStatementBatch['items']) =>
  items.filter((i) => i.status === 'CONFIRMED').length;

export function useInvestorStatementGeneration(
  investorExternalId: string,
  onLibraryChanged?: () => void,
) {
  const [batch, setBatch] = useState<InvestorStatementBatch | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollTimeoutRef = useRef<number | null>(null);
  const backendRef = useRef(new BackendService());

  const clearPollingTimeout = useCallback(() => {
    if (pollTimeoutRef.current !== null) {
      window.clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => clearPollingTimeout, [clearPollingTimeout]);

  const pollBatch = useCallback(
    (batchId: string) => {
      const poll = async (): Promise<void> => {
        try {
          const latest = await backendRef.current.getInvestorStatementBatch(batchId);
          setBatch(latest);
          if (latest.status === 'COMPLETED' || latest.status === 'FAILED') {
            clearPollingTimeout();
            setGenerating(false);
          } else {
            pollTimeoutRef.current = window.setTimeout(() => void poll(), POLL_INTERVAL_MS);
          }
        } catch (err: unknown) {
          clearPollingTimeout();
          setGenerating(false);
          setError(
            err instanceof Error ? err.message : 'Failed to check statement generation progress',
          );
        }
      };
      void poll();
    },
    [clearPollingTimeout],
  );

  const generate = useCallback(async () => {
    setGenerating(true);
    setError(null);
    setBatch(null);
    try {
      const { batchId } =
        await backendRef.current.generateInvestorStatementBatch(investorExternalId);
      pollBatch(batchId);
    } catch (err: unknown) {
      setGenerating(false);
      setError(err instanceof Error ? err.message : 'Failed to generate investor statements');
    }
  }, [investorExternalId, pollBatch]);

  // Merge reviewed items back into the batch; a rise in the confirmed count means
  // a statement was just added to the investor's document library.
  const applyItems = useCallback(
    (items: InvestorStatementBatch['items']) => {
      setBatch((prev) => {
        if (!prev) return prev;
        if (confirmedCount(items) > confirmedCount(prev.items)) onLibraryChanged?.();
        return { ...prev, items };
      });
    },
    [onLibraryChanged],
  );

  return { batch, generating, error, generate, setBatch, applyItems };
}
